export interface Wallet {
  walletAddress: string;
  jettonAddress: string;
  jettonBalance: number;
}

export interface Deposit {
  totalDeposit: number;
}

export interface MinDeposit {
  minTonAmount: number;
  minTokenAmount: number;
}

export interface Rewards {
  totalRewardsBalance: number;
  totalRewardsBalanceUsd: number;
  tomorrowRewards: number;
  nextStartTimeWithdraw: string;
  nextEndTimeWithdraw: string;
  withdrawable: boolean;
}

export interface Airdrop {
  totalAirdropBalance: number;
  transactionFee: number;
  minWithdraw: number;
}

export interface WithdrawAirdrop {
  withdrawalId: string;
}

export interface TonConnectMsg {
  address: string;
  amount: string;
  payload: string;
}

export interface DepositPayload {
  tonConnectMsg: TonConnectMsg;
}

export interface DepositReward {
  depositId: string;
  tokenAmount: number;
  usdAmount: number;
}

export interface WithdrawDepositRewards {
  totalTokenAmount: number;
  nextStartTimeWithdraw: string;
  nextEndTimeWithdraw: string;
  depositsRewards: DepositReward[];
  existingTokenAmount: number;
}

export interface MatchingBonusItem {
  id: string;
  depositRewardId: string;
  fromReferralId: string;
  toReferralId: string;
  generation: Generation;
  rankCode: RankCode;
  baseAmount: string;
  percent: string;
  bonusAmount: number;
  status: string;
  withdrawalId: string | null;
  createdAt: string;
}

export type Generation = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;

export const RANK_ORDER: Record<RankCode, number> = {
  BRONZE: 1,
  SILVER: 2,
  GOLD: 3,
  PLATINUM: 4,
  SAPPHIRE: 5,
  RUBY: 6,
  EMERALD: 7,
  DIAMOND: 8,
  BLUE_DIAMOND: 9,
  MILLION_CLUB: 10,
};

export const RANK_CODES: RankCode[] = [
  "BRONZE",
  "SILVER",
  "GOLD",
  "PLATINUM",
  "SAPPHIRE",
  "RUBY",
  "EMERALD",
  "DIAMOND",
  "BLUE_DIAMOND",
  "MILLION_CLUB",
];

export type RankCode =
  | "BRONZE"
  | "SILVER"
  | "GOLD"
  | "PLATINUM"
  | "SAPPHIRE"
  | "RUBY"
  | "EMERALD"
  | "DIAMOND"
  | "BLUE_DIAMOND"
  | "MILLION_CLUB";

export const CAPS = {
  GOLD_OR_LOWER: 5000,
  PLATINUM: 15000,
  SAPPHIRE: 35000,
  RUBY: 50000,
  EMERALD_OR_HIGHER: 75000,
};

export function getPromoCapByCurrentRank(code?: RankCode): number {
  if (!code) return CAPS.GOLD_OR_LOWER;

  const order = (c: RankCode) => RANK_ORDER[c] ?? 0;

  if (order(code) <= RANK_ORDER.GOLD) return CAPS.GOLD_OR_LOWER;
  if (code === "PLATINUM") return CAPS.PLATINUM;
  if (code === "SAPPHIRE") return CAPS.SAPPHIRE;
  if (code === "RUBY") return CAPS.RUBY;

  return CAPS.EMERALD_OR_HIGHER;
}

export function getNextPromoCapAndRank(promoTurnover: number): {
  cap: number;
  code: RankCode;
} {
  const orderedCaps: Array<{ code: RankCode; cap: number }> = [
    { code: "GOLD", cap: CAPS.GOLD_OR_LOWER },
    { code: "PLATINUM", cap: CAPS.PLATINUM },
    { code: "SAPPHIRE", cap: CAPS.SAPPHIRE },
    { code: "RUBY", cap: CAPS.RUBY },
    { code: "EMERALD", cap: CAPS.EMERALD_OR_HIGHER },
  ];

  const next = orderedCaps.find(({ cap }) => promoTurnover < cap);

  return next ?? orderedCaps[orderedCaps.length - 1];
}

export interface RankPayout {
  generation: Generation;
  percent: number;
}

export interface MatchingBonusProgressItem {
  current: number;
  required: number;
  percentage: number;
}

export interface MatchingBonusProgress {
  activeDirects?: MatchingBonusProgressItem;
  activeInFirstNLevels?: MatchingBonusProgressItem;
  personalVolume?: MatchingBonusProgressItem;
}

export interface MatchingBonusPayout {
  generation: number;
  percent: number;
}

export interface MatchingBonusReward {
  minPercent: number;
  maxPercent: number;
  generations: number;
  payouts: RankPayout[];
}

export interface MatchingBonusRankLevel {
  code: RankCode;
  title: string;
  rewards: MatchingBonusReward;
  progress?: MatchingBonusProgress;
}

export interface MatchingBonusRank {
  current: MatchingBonusRankLevel;
  next: MatchingBonusRankLevel;
}

export interface MatchingBonus {
  tokenAmount: number;
  usdAmount: number;
  tonAmount: number;
  nextStartTimeWithdraw: string;
  nextEndTimeWithdraw: string;
  bonuses: MatchingBonusItem[];
  rank: MatchingBonusRank;
  promoTurnover: number;
}

export interface Deposits {
  deposits: DepositsItem[];
}

export interface DepositsItem {
  deposit: DepositChild;
  reward: Reward;
}

export type DepositStatus = "Created" | "Active" | "Closed" | "Cancelled";

export interface DepositChild {
  id: string;
  userId: string;
  status: DepositStatus;
  tonAmount: number;
  usdAmount: number;
  tokenAmount: number;
  withdrawnTokenAmount: number;
  transaction: string;
  fromAddress: string;
  toAddress: string;
  createdAt: string;
  tokenUsdPrice: number;
  network: string;
}

export interface Reward {
  endDate: RewardDateDetail;
  targetDateTotal: RewardDateDetail;
  targetDateAvailable: RewardDateDetail;
  withdrawnTokenAmount: number;
}

export interface RewardDateDetail {
  day: number;
  dayNoFromCreation: number;
  date: string;
  amount: number;
  unfolded: UnfoldedReward;
}

export interface UnfoldedReward {
  user: number;
  commission: number;
  dev: number;
  vesting: number;
}

export interface ReinvestedDeposit {
  id: string;
  userId: string;
  status: string;
  tonAmount: string;
  usdAmount: string;
  tokenAmount: string;
  transaction: string;
  fromAddress: string;
  toAddress: string;
  withdrawnTokenAmount: string;
  processedMatchingBonusTokenAmount: string;
  thresholdCoef: number | null;
  activatedAt: string | null;
  updatedAt: string;
  createdAt: string;
  locked: boolean;
}

export interface PostReinvestDepositRewardBonusesResponse {
  reinvestedDeposit: ReinvestedDeposit;
}

export interface PostReinvestAirdropsResponse {
  reinvestedDeposit: ReinvestedDeposit;
}
